/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

// TODO #295 Bug v eslint.
/* eslint-disable jsdoc/require-returns */

import Numeric from '../numeric/numeric';

// TODO Chybí podpora pro drain:
// - Sice je přítomná podobná metoda, ale ta neumožňuje vybrat počet
//   a po jejím volání je již pool nepoužitelný.
// - "One side-effect of calling drain() is that subsequent calls to acquire() will throw an Error."
// TODO Chybí podpora pro neomezený pool.
// - https://github.com/coopernurse/node-pool/issues/264
// TODO Hotfix implementace metody `.fill`, viz. popis metody.
// - https://github.com/coopernurse/node-pool/issues/265
// TODO Chybí podpora pro operace s duplicitními objekty, které může továrna vytvořit.
// - Použitá knihovna pravděpodobně eviduje objekty podle jejich identity.
// - Zatím není používáno, takže byly pouze odstraněny testy.
// - Nebylo nic reportováno, nejde o "must-have" funkcionalitu.
/**
 * Asynchronní bazén recyklovaných objektů.
 * Implementuje návrhový vzor [Object pool](https://en.wikipedia.org/wiki/Object_pool_pattern).
 * Provádí recyklaci vrácených objektů pro další použití.
 * Počet objektů lze omezit maximem (nedovolí vytváření dalších).
 * Narozdíl od obyčejného bazénu na objekty
 * lze provádět asynchronní operace během vytváření a mazání objektů.
 *
 * Implementace je orientována na maximální výkon, jednoduchost a robustnost.
 *
 * Lze využít pro redukci garbage collection, omezení náročné inicializace nebo destrukce,
 * splnění omezení na přístup ke zdrojům apod.
 *
 * Inspirováno existujícími knihovnami:
 * [generic-pool](https://github.com/coopernurse/node-pool),
 * [deePool](https://github.com/getify/deePool).
 */
class PoolAsync {
  /**
   * Vytvoří instanci.
   * @param {PoolObjectFactory} factory Továrna pro vytváření nových objektů umísťených v bazénu.
   * @param {GenericPool} genericPool
   * @param {number} max Maximální počet objektů, které má bazén vytvořit.
   */
  constructor(factory, genericPool, max) {
    const pool = genericPool.createPool(
      {
        create() {
          return factory.create();
        },
        destroy(object) {
          return factory.delete(object);
        }
      },
      {
        // Zahájení plnění v konstruktoru je nežádoucí.
        autostart: false,
        // Omezení počtu objektů v bazénu.
        // TODO Chybí podpora pro neomezený pool.
        // eslint-disable-next-line no-ternary
        max: max === Infinity ? Number.MAX_SAFE_INTEGER : max,
        // Čekání na uvolnění zdrojů je nežádoucí.
        maxWaitingClients: 0
      }
    );
    // TODO Hotfix problému se zacyklením pool při selhání `factory.create`.
    // - Dle autora je již problém známý několik let a stále není opravený.
    // - Je použitý hotfix s přístupem k private metodám.
    // - https://github.com/coopernurse/node-pool/issues/175#issuecomment-501826583
    pool.on('factoryCreateError', (error) => {
      // eslint-disable-next-line no-underscore-dangle
      pool._waitingClientsQueue.dequeue().reject(error);
    });
    this.pool = pool;
  }

  /**
   * Obdrží zpět poškozený zapůjčený objekt.
   * Takový objekt není dále recyklovaný a nahradí se při další zápůjčce za nový.
   * Volající by měl používat tuto metodu, pokud již objekt nelze obnovit do stavu pro další použití.
   * @param {PoolObject} object Vrácený objekt.
   * @returns {Promise.<undefined>}
   */
  delete(object) {
    return new Promise((resolve) => {
      const { pool } = this;
      if (!pool.isBorrowedResource(object)) {
        throw new Error(`Object ${object} is not lent.`);
      }
      return resolve(pool.destroy(object));
    });
  }

  /**
   * Uvolní všechny zdroje pro garbage collection.
   * Počká na vrácení všech zdrojů a uvolní je.
   * @returns {Promise.<undefined>}
   */
  destroy() {
    return new Promise((resolve) => {
      const { pool } = this;
      return resolve(pool.drain().then(() => pool.clear()));
    });
  }

  /**
   * Naplní bazén objektů, které jsou pak ihned připravené k zapůjčení.
   *
   * Funguje špatně, ale používá se jen v testech.
   * Využívá privátní členy začínající podtržítkem,
   * u kterých není zaručena kompatibilita.
   * Použitá knihovna neumožňuje čekat na naplnění.
   * @param {number} count Počet objektů.
   * @returns {Promise.<undefined>}
   */
  fill(count) {
    return new Promise((resolve) => {
      const { pool } = this;
      if (!Numeric.isIndex(count)) {
        throw new TypeError(`Invalid count ${count}.`);
      }
      if (count > this.getMax() - this.getLength()) {
        throw new Error('Maximum count of objects exceeded.');
      }
      // eslint-disable-next-line no-underscore-dangle
      pool._config.min = pool._count + count;
      // eslint-disable-next-line no-underscore-dangle
      pool._ensureMinimum();
      // TODO Hotfix čekání na naplnění pool.
      setTimeout(resolve, 0);
    });
  }

  /**
   * Zjistí počet volných objektů, připravených na zapůjčení.
   * @returns {number} Počet objektů.
   */
  getAvailable() {
    return this.pool.available;
  }

  /**
   * Zjistí aktuální počet vytvořených objektů v bazénu.
   * @returns {number} Počet objektů.
   */
  getLength() {
    return this.pool.size;
  }

  /**
   * Zjistí počet zapůjčených objektů.
   * @returns {number} Počet objektů.
   */
  getLent() {
    return this.pool.borrowed;
  }

  /**
   * Zjistí maximální počet vytvořených objektů v bazénu.
   * @returns {number} Počet objektů, nebo `Infinity` pokud není omezeno.
   */
  getMax() {
    // TODO Chybí podpora pro neomezený pool.
    const { max } = this.pool;
    if (max === Number.MAX_SAFE_INTEGER) {
      return Infinity;
    }
    return max;
  }

  /**
   * Ověří, jestli objekt patří mezi zapůjčené.
   * @param {PoolObject} object
   * @returns {boolean}
   */
  isLent(object) {
    return this.pool.isBorrowedResource(object);
  }

  /**
   * Zapůjčí volný objekt z bazénu.
   * @returns {Promise.<PoolObject>} Přidělený objekt, pokud je nějaký volný.
   */
  lend() {
    return new Promise((resolve) => resolve(this.pool.acquire()));
  }

  /**
   * Obdrží zpět zapůjčený objekt.
   * Takový objekt je dále recyklovaný pro nové zapůjčení.
   * Po volání této metody je zakázáno k vrácenému objektu přistupovat, nebo ho dokonce měnit.
   * Volající je také zodpovědný za to, že objekt je v takovém stavu, v jakém byl při zapůjčení.
   * @param {PoolObject} object Vrácený objekt.
   * @returns {undefined}
   */
  receive(object) {
    const { pool } = this;
    if (!pool.isBorrowedResource(object)) {
      throw new Error(`Object ${object} is not lent.`);
    }
    pool.release(object);
  }
}

export default PoolAsync;
